import { ART_PALETTE } from '../art-palette.js';
import { BLOB_MASKS, MASK } from './masks.js';
import { blankPixels, hash, plainTile } from './helpers.js';

const INK = 1;
const CREAM = 11;
const SLATE = 29;
const STONE = 26;

/**
 * The house-style structure trim (art-direction §0.2): Ink contour, a one-pixel
 * Cream band outside it, then a second Ink contour outside that. Addressed by
 * depth from the free edge so it wraps every silhouette the same way and two
 * tiles sharing a free side always agree pixel for pixel.
 */
const TRIM = [INK, CREAM, INK];

/**
 * Exterior shell walls (top-down Blob-47).
 *
 * One flat field per material with the trim on the outward faces and a single
 * Ink contour where the ring meets the room. Depth is measured from the nearest
 * free edge, so corners miter themselves into concentric rings and the inner
 * contours of adjacent ring tiles close into one rectangle around the room.
 */
function exteriorVariants({ body }) {
  return Object.fromEntries(BLOB_MASKS.map((mask) => {
    const pixels = blankPixels(body);
    const freeNorth = !(mask & MASK.N);
    const freeEast = !(mask & MASK.E);
    const freeSouth = !(mask & MASK.S);
    const freeWest = !(mask & MASK.W);
    if (!(freeNorth || freeEast || freeSouth || freeWest)) return [mask, pixels];
    for (let y = 0; y < 16; y++) {
      for (let x = 0; x < 16; x++) {
        const depth = Math.min(
          freeNorth ? y : 99,
          freeSouth ? 15 - y : 99,
          freeWest ? x : 99,
          freeEast ? 15 - x : 99,
        );
        if (depth < TRIM.length) pixels[y][x] = TRIM[depth];
        else if (depth === 15) pixels[y][x] = INK;
      }
    }
    return [mask, pixels];
  }));
}

/**
 * South facade strip (pipe-4 horizontal): the front wall standing up below the
 * shell. Flat field, an occlusion band where the roof eave shades it, and the
 * slate foundation the whole house family shares.
 */
function exteriorFacadeVariants({ body, shade }) {
  const horizontalMasks = [0, MASK.E, MASK.W, MASK.E | MASK.W];
  return Object.fromEntries(horizontalMasks.map((mask) => {
    const pixels = blankPixels(body);
    for (let y = 0; y < 16; y++) {
      for (let x = 0; x < 16; x++) {
        if (y < 4) pixels[y][x] = shade;         // under-eave occlusion
        else if (y === 11) pixels[y][x] = STONE; // lit top of the foundation
        else if (y > 11) pixels[y][x] = SLATE;
      }
    }
    // Ground contact plus any free end of the run: same trim, mitered by depth.
    for (let y = 0; y < 16; y++) {
      for (let x = 0; x < 16; x++) {
        const depth = Math.min(
          15 - y,
          (mask & MASK.W) ? 99 : x,
          (mask & MASK.E) ? 99 : 15 - x,
        );
        if (depth < TRIM.length) pixels[y][x] = TRIM[depth];
      }
    }
    return [mask, pixels];
  }));
}

function exteriorTile(key, name, colors) {
  return {
    key, name, channel: 'exterior', mode: 'blob8', connectionGroup: key,
    paletteSection: 'exteriors', palette: ART_PALETTE, collision: { wall: true }, shell: true,
    components: {
      wall: exteriorVariants(colors),
      onGround: exteriorFacadeVariants(colors),
    },
  };
}

const SLOPE = 4;   // pixels of visible slope on each side of the ridge deck
const RIDGE = 3;   // depth at which a sloped side starts (just inside the trim)
const COURSE = 8;  // roof course pitch; divides 16, so the rhythm never marks the cell

/**
 * Roof deck.
 *
 * §0.4 was written for a single-sprite house, where "one flat plane with a
 * single ridge line" is the whole roof. A tiled roof is thirty-five of these
 * cells, and a fully surrounded cell has no perimeter to describe it: filled
 * with nothing but `body` it turns the middle of any real building into an
 * unbroken colour field that reads as a hole, not a surface.
 *
 * So the deck carries the minimum structure that says "roof": slate courses at
 * a single ramp step under the body. The pitch divides 16, exactly like the
 * plank separators in `floors.js`, so the rhythm is uniform across every join
 * and the eye can never find the cell boundary from it.
 *
 * The courses are unbroken on purpose. Anything drawn *between* them — a dash,
 * a speck, a run of shingles — has to repeat on the 16px pitch, because a
 * fully surrounded cell has only one frame, and that repetition is legible as a
 * stamped column the moment a roof is more than two tiles wide. The only other
 * mark is a single butt joint hanging off each course on a hashed lattice:
 * attached to a continuous line it reads as a joint rather than as a floating
 * motif, and one per course is scarce enough that the 16px recurrence never
 * assembles into a column. No second value, no vertical course, no shingle grid.
 */
function paintDeck(pixels, course) {
  // Phased off the cell edge: the first course sits at 3, so the outer seam
  // band stays plain body and two decks join without a line landing on the
  // boundary row itself. Spacing across a join is still exactly COURSE.
  for (let y = 3; y < 16; y += COURSE) {
    for (let x = 0; x < 16; x++) pixels[y][x] = course;
    const x = Math.floor(hash(y * 7 + 3, 29) * 16);
    const drop = 1 + Math.floor(hash(13, y * 5 + 9) * 3);
    for (let i = 1; i <= drop && y + i < 16; i++) pixels[y + i][x] = course;
  }
}

/**
 * Roof Blob-47 fill.
 *
 * A busy field reads as another wall from above, so the roof stays one plane
 * carrying only the deck courses above, and its silhouette does the work. Every
 * outward side gets a slope band inside the trim — lit toward the upper-left key
 * light, dark away from it — which makes the whole footprint read as a hipped
 * roof with a flat ridge deck. The front slope keeps going past the tile into
 * the eave drawn one cell lower.
 */
function roofVariants(kind, { body, light, dark, course = dark }) {
  return Object.fromEntries(BLOB_MASKS.map((mask) => {
    const pixels = blankPixels(body);
    const free = {
      north: !(mask & MASK.N), east: !(mask & MASK.E),
      south: !(mask & MASK.S), west: !(mask & MASK.W),
    };
    const freeNorth = free.north;
    const freeEast = free.east;
    const freeSouth = free.south;
    const freeWest = free.west;

    if (kind === 'straw') {
      // Thatch has no courses: short comb strokes on a jittered lattice,
      // wrapped in x so a run of tiles reads as continuous thatch.
      for (let y = 1; y < 16; y += 4) {
        for (let x = 0; x < 16; x += 5) {
          const row = y + (hash(x, y) > 0.55 ? 1 : 0);
          const stroke = hash(y, x) > 0.7 ? dark : light;
          for (let i = 0; i < 3; i++) pixels[row][(x + i + Math.floor(hash(x, y) * 3)) % 16] = stroke;
        }
      }
    } else {
      paintDeck(pixels, course);
    }

    for (let y = 0; y < 16; y++) {
      for (let x = 0; x < 16; x++) {
        const depths = {
          north: freeNorth ? y : 99,
          south: freeSouth ? 15 - y : 99,
          west: freeWest ? x : 99,
          east: freeEast ? 15 - x : 99,
        };
        // The trim wraps the silhouette, so it is resolved from the nearest
        // *trimmed* side and wins outright. The front slope has no trim of its
        // own (it hands its contour to the eave), and on a south-west or
        // south-east corner it is the nearest side for the last few pixels —
        // which used to punch a three-pixel hole straight through the cream
        // band exactly where it turns the corner.
        const trimDepth = Math.min(depths.north, depths.west, depths.east);
        if (trimDepth < TRIM.length) { pixels[y][x] = TRIM[trimDepth]; continue; }
        // Otherwise the nearest outward side owns the pixel, so corners hip
        // themselves.
        const side = Object.keys(depths).reduce((best, key) =>
          (depths[key] < depths[best] ? key : best), 'north');
        const depth = depths[side];
        if (depth > 15) continue;
        const lit = side === 'north' || side === 'west';
        // The front slope starts at the tile edge; every other side has already
        // spent its first three pixels on the trim.
        const start = side === 'south' ? 0 : RIDGE;
        if (depth >= start && depth < start + SLOPE) pixels[y][x] = lit ? light : dark;
        else if (depth === start + SLOPE && lit) pixels[y][x] = dark; // ridge seam
      }
    }
    if (kind === 'parapet') {
      // A parapet is a wall top, not a slope: crenellate the outer contour.
      const notch = (along) => Math.floor(along / 4) % 2 === 1;
      const merlon = (along, side) => {
        for (let depth = 0; depth < 5; depth++) {
          const x = side === 'west' ? depth : side === 'east' ? 15 - depth : along;
          const y = side === 'north' ? depth : along;
          pixels[y][x] = depth === 4 ? dark : notch(along) ? 0 : TRIM[Math.min(depth, 2)];
        }
      };
      for (let along = 0; along < 16; along++) {
        if (freeNorth) merlon(along, 'north');
        if (freeWest) merlon(along, 'west');
        if (freeEast) merlon(along, 'east');
      }
    }
    return [mask, pixels];
  }));
}

/**
 * Roof eave (pipe-4 horizontal), drawn one cell south of the roof's front edge.
 * Without it the roof stops flush with the wall and the building reads as a
 * stack of flat rectangles; the overhang is what puts the roof *on top*.
 */
function roofEaveVariants({ dark }) {
  const horizontalMasks = [0, MASK.E, MASK.W, MASK.E | MASK.W];
  return Object.fromEntries(horizontalMasks.map((mask) => {
    const pixels = blankPixels();
    for (let x = 0; x < 16; x++) {
      pixels[0][x] = dark;  // front slope, continued past the wall line
      pixels[1][x] = dark;
      pixels[2][x] = INK;   // fascia contour
      pixels[3][x] = 2;     // shadow the overhang throws down the wall
    }
    // Free ends of the run close with the structure trim. It has to cover the
    // wall-shadow row too, or the band breaks for one pixel between the eave
    // and the facade's own trim underneath.
    for (let y = 0; y < 4; y++) {
      for (let x = 0; x < 16; x++) {
        const depth = Math.min(
          (mask & MASK.W) ? 99 : x,
          (mask & MASK.E) ? 99 : 15 - x,
        );
        if (depth < TRIM.length) pixels[y][x] = TRIM[depth];
      }
    }
    return [mask, pixels];
  }));
}

function roofTile(key, name, kind, colors) {
  const components = { roof: roofVariants(kind, colors) };
  // A parapet crowns a wall; only sloped roofs overhang.
  if (kind !== 'parapet') components.eave = roofEaveVariants(colors);
  return {
    key, name, channel: 'roof', mode: 'blob8', connectionGroup: key,
    paletteSection: 'roofs', palette: ART_PALETTE, collision: {},
    components,
  };
}

// A facade cell is only 16px tall and the roof eave takes the top four, so wall
// openings live between the eave and the ground contour the facade owns.
const OPENING_TOP = 4;
const OPENING_BASE = 14;

/**
 * Door: flat leaf inside a full Ink frame, one lit strip across the head, one
 * knob. Reaches the ground contour so the entrance breaks the foundation band.
 */
function doorPixels(color, lit) {
  const p = blankPixels();
  for (let y = OPENING_TOP; y <= OPENING_BASE; y++) {
    for (let x = 4; x < 12; x++) p[y][x] = INK;
  }
  for (let y = OPENING_TOP + 1; y <= OPENING_BASE; y++) {
    for (let x = 5; x < 11; x++) p[y][x] = color;
  }
  for (let x = 5; x < 11; x++) p[OPENING_TOP + 1][x] = lit;
  p[9][9] = 10;
  return p;
}

/**
 * Window: slate frame, dark glass, one cream sill. The style board's cottage
 * window at tile scale — no per-pane highlight, no wood grain.
 */
function windowPixels(round = false, curtains = false) {
  const p = blankPixels();
  const top = OPENING_TOP;
  const bottom = 9; // clear of the foundation band the facade owns below
  for (let y = top; y <= bottom; y++) {
    for (let x = 3; x < 13; x++) {
      const inside = !round || ((x - 7.5) ** 2 + (y - (top + bottom) / 2) ** 2 < 20);
      if (inside) p[y][x] = SLATE;
    }
  }
  for (let y = top + 2; y < bottom - 1; y++) {
    for (let x = 5; x < 11; x++) {
      const inside = !round || ((x - 7.5) ** 2 + (y - (top + bottom) / 2) ** 2 < 12);
      if (inside) p[y][x] = 2;
    }
  }
  // One mullion cross, in the frame colour.
  for (let y = top + 2; y < bottom - 1; y++) p[y][7] = SLATE;
  for (let x = 5; x < 11; x++) p[top + 4][x] = SLATE;
  // Stone lintel and cream sill, the way the reference board frames openings.
  for (let x = 3; x < 13; x++) p[top][x] = STONE;
  for (let x = 2; x < 14; x++) p[bottom + 1][x] = CREAM;
  if (curtains) {
    for (let y = top + 1; y <= bottom; y++) {
      p[y][3] = 5;
      p[y][4] = 5;
      p[y][11] = 5;
      p[y][12] = 5;
    }
  }
  return p;
}

export { windowPixels };

export const EXTERIOR_AND_ROOF_TILES = [
  // Every shell is one flat field wrapped in the shared structure trim, so the
  // family reads as one style; only the material's three values change.
  exteriorTile('exterior.brick', 'Brick exterior', { body: 5, shade: 4 }),
  // Cool stone ramp 24-27.
  exteriorTile('exterior.stone', 'Stone exterior', { body: 26, shade: 24 }),
  exteriorTile('exterior.stucco-cream', 'Cream stucco exterior', { body: 10, shade: 7 }),
  exteriorTile('exterior.stucco-rose', 'Rose stucco exterior', { body: 6, shade: 4 }),
  exteriorTile('exterior.stucco-sage', 'Sage stucco exterior', { body: 16, shade: 13 }),
  exteriorTile('exterior.stucco-blue', 'Blue stucco exterior', { body: 20, shade: 18 }),
  // Roof ramps are chosen against the ground they sit on, not in isolation. A
  // roof that walks the same three indices as a surface tile dissolves into it —
  // `roof.green-tile` used to be Grass / Grass light / Forest, the exact ramp of
  // `surface.grass`, so a green roof was painted in the colours of its own lawn.
  // Every roof below is anchored on a cool or built index that no terrain uses
  // as a field, and none shares a full ramp with grass (12-16), dirt (7-8),
  // sand coast (7, 9) or rock (24-27).
  roofTile('roof.castle-parapet', 'Castle parapet roof', 'parapet', {
    // Cool masonry. Rock terrain's field is Stone (26), so a Stone dark crown
    // still steps away from the ground even on a cliff top.
    body: 25, light: 27, dark: 24, course: 24,
  }),
  // Thatch. Sand light stays for the lit comb, but the shadow drops to Brown
  // shadow instead of Earth dark, so the roof no longer walks the sand
  // coastline's own two indices (Sand 9 + Earth dark 7).
  roofTile('roof.straw', 'Straw roof', 'straw', { body: 9, light: 10, dark: 3 }),
  // Dark cool slate: Stone shadow plane, Stone lit slope, Ink eave, and the
  // courses one step down into Deep shadow.
  roofTile('roof.black-tile', 'Black tile roof', 'tile', {
    body: 24, light: 26, dark: 1, course: 2,
  }),
  // The style board's roof (artstyle/exterior.jpg): a muted blue-green slate
  // plane with Slate blue ridge, eave and courses. Water dark reads as
  // weathered copper-green against every land surface in the set.
  roofTile('roof.green-tile', 'Green tile roof', 'tile', {
    body: 19, light: 20, dark: 29, course: 29,
  }),
  // Slate blue proper — the cool cloth/roof support index of §7 — with a Stone
  // lit slope. Desaturated well clear of the 18-22 water ramp.
  roofTile('roof.blue-tile', 'Blue tile roof', 'tile', {
    body: 29, light: 26, dark: 24, course: 24,
  }),
  // Doors stay a step darker than any wall body so they read on every shell.
  ...[['oak', 3, 7], ['blue', 18, 19], ['green', 12, 13]].map(([name, color, lit]) => plainTile({
    key: `exterior-object.door-${name}`,
    name: `${name[0].toUpperCase() + name.slice(1)} exterior door`,
    channel: 'wallObject',
    section: 'exteriorObjects',
    component: 'wall',
    pixels: doorPixels(color, lit),
    collision: {},
    opensWall: true,
  })),
  plainTile({
    key: 'exterior-object.window-square',
    name: 'Square exterior window',
    channel: 'wallObject',
    section: 'exteriorObjects',
    component: 'wall',
    pixels: windowPixels(),
    collision: { wall: true },
  }),
  plainTile({
    key: 'exterior-object.window-round',
    name: 'Round exterior window',
    channel: 'wallObject',
    section: 'exteriorObjects',
    component: 'wall',
    pixels: windowPixels(true),
    collision: { wall: true },
  }),
];
